import { useState, useCallback } from 'react';
import { uuid } from '../utils';

export interface PopoutEntry {
  id: string;
  sheetId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
}

export interface UsePopoutReturn {
  popouts: PopoutEntry[];
  add: (sheetId: string, width?: number, height?: number) => void;
  remove: (id: string) => void;
  bringToFront: (id: string) => void;
  updatePosition: (id: string, x: number, y: number) => void;
  updateSize: (id: string, width: number, height: number) => void;
}

let nextZ = 2000;

export function usePopout(): UsePopoutReturn {
  const [popouts, setPopouts] = useState<PopoutEntry[]>([]);

  const add = useCallback((sheetId: string, width = 400, height = 500) => {
    setPopouts(prev => {
      // Don't open duplicate for same sheet
      if (prev.some(p => p.sheetId === sheetId)) return prev;
      nextZ++;
      const entry: PopoutEntry = {
        id: uuid(),
        sheetId,
        x: 100 + prev.length * 30,
        y: 100 + prev.length * 30,
        width,
        height,
        zIndex: nextZ,
      };
      return [...prev, entry];
    });
  }, []);

  const remove = useCallback((id: string) => {
    setPopouts(prev => prev.filter(p => p.id !== id));
  }, []);

  const bringToFront = useCallback((id: string) => {
    nextZ++;
    setPopouts(prev =>
      prev.map(p => (p.id === id ? { ...p, zIndex: nextZ } : p)),
    );
  }, []);

  const updatePosition = useCallback((id: string, x: number, y: number) => {
    setPopouts(prev =>
      prev.map(p => (p.id === id ? { ...p, x, y } : p)),
    );
  }, []);

  const updateSize = useCallback((id: string, width: number, height: number) => {
    setPopouts(prev =>
      prev.map(p => (p.id === id ? { ...p, width, height } : p)),
    );
  }, []);

  return { popouts, add, remove, bringToFront, updatePosition, updateSize };
}
